import { useEffect, useState, type ReactNode } from 'react';
import './RegistrationDialog.css';

type DialogMode = 'login' | 'register';

const DEFAULT_REGISTRATION_CONSENT_TEXT =
  'Alchemie collects your name, institution, and email in order to send you demo access to this product, as well as any relevant information or updates. We will not sell or otherwise distribute this information. You may need to check your spam folder.';

function EyeIcon({ crossed }: { crossed: boolean }) {
  if (crossed) {
    return (
      <svg
        className="registration-password-toggle-icon"
        viewBox="0 0 24 24"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <path
          d="M3 3L21 21"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M10.7 6.2C11.13 6.07 11.56 6 12 6C16.6 6 20.2 8.5 22 12C21.3 13.35 20.39 14.53 19.32 15.46"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M14.1 14.1C13.54 14.67 12.78 15 12 15C10.34 15 9 13.66 9 12C9 11.22 9.33 10.46 9.9 9.9"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M6.04 8.7C4.48 9.57 3.17 10.72 2 12C3.8 15.5 7.4 18 12 18C13.53 18 14.96 17.72 16.24 17.21"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  return (
    <svg
      className="registration-password-toggle-icon"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M2 12C3.8 8.5 7.4 6 12 6C16.6 6 20.2 8.5 22 12C20.2 15.5 16.6 18 12 18C7.4 18 3.8 15.5 2 12Z"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.8" />
    </svg>
  );
}

export interface RegistrationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: RegistrationFormData) => void | Promise<void>;
  onLoginSubmit?: (data: LoginFormData) => void | Promise<void>;
  onForgotPasswordClick?: () => void;
  onForgotPasswordSubmit?: (email: string) => void | Promise<void>;
  onResendVerificationEmail?: () => void | Promise<void>;
  /** After successful create-account submit, switch to login tab and show a success hint. */
  switchToLoginAfterSuccessfulRegistration?: boolean;
  /** Prefill login email when opening in login mode (e.g. after navigating from signup). */
  initialLoginEmail?: string;
  /** Show a success banner on the login form (e.g. after account creation). */
  initialPostRegisterSuccessMessage?: string;
  /**
   * When set (typically with `onSwitchToLogin`), "Create one" / "Log in" use these instead of
   * in-dialog mode switching — e.g. navigate to `/signup` and `/login` so the URL matches the form.
   */
  onSwitchToRegister?: () => void;
  onSwitchToLogin?: () => void;
  initialMode?: DialogMode;
  title?: string;
  subtitle?: string;
  /**
   * Shown above the main title (e.g. “by Alchemie”) with optional wordmark before it.
   * When set, both login and create-account use the same navy header, kicker row, and padded form layout.
   */
  registerBrandKicker?: string;
  /**
   * When true, a privacy / consent checkbox must be checked to submit. Submitted as `marketingConsent: true` on the form data.
   * @default false
   */
  requireRegistrationConsent?: boolean;
  /**
   * When false, hides the secondary Cancel button and uses a full-width primary action (login and create-account).
   * @default true
   */
  registerShowCancel?: boolean;
  /** Content next to the consent checkbox when `requireRegistrationConsent` is true. */
  registrationConsentText?: ReactNode;
  /**
   * Optional brand image shown before the register kicker (e.g. `registerBrandKicker`, “by alchemie”).
   * Use a URL or Vite `import` result; for light-on-transparent artwork, use with dark background in CSS.
   */
  registerTitleLogoSrc?: string;
  /** `alt` for that image; use an empty string if decorative. @default "Piph" */
  registerTitleLogoAlt?: string;
}

export interface RegistrationFormData {
  name: string;
  email: string;
  institution?: string;
  /** Required for create-account flows that use Firebase Auth; not sent to the license API. */
  password?: string;
  productKey?: string;
  /** When `requireRegistrationConsent` is true, the user must accept; stored in app backends as needed, not on license API. */
  marketingConsent?: boolean;
}

export interface LoginFormData {
  email: string;
  password: string;
}

export function RegistrationDialog({
  isOpen,
  onClose,
  onSubmit,
  onLoginSubmit,
  onForgotPasswordClick,
  onForgotPasswordSubmit,
  onResendVerificationEmail,
  switchToLoginAfterSuccessfulRegistration = false,
  initialLoginEmail,
  initialPostRegisterSuccessMessage,
  onSwitchToRegister,
  onSwitchToLogin,
  initialMode = 'login',
  title = 'Product Registration',
  subtitle = 'Please register to continue using this product',
  registerBrandKicker,
  requireRegistrationConsent = false,
  registerShowCancel = true,
  registrationConsentText,
  registerTitleLogoSrc,
  registerTitleLogoAlt = 'Piph',
}: RegistrationDialogProps) {
  const [mode, setMode] = useState<DialogMode>(initialMode);
  const MIN_PASSWORD_LENGTH = 6;
  const resolvedConsentText =
    typeof registrationConsentText === 'string'
      ? registrationConsentText.trim() || DEFAULT_REGISTRATION_CONSENT_TEXT
      : registrationConsentText || DEFAULT_REGISTRATION_CONSENT_TEXT;

  const [registrationData, setRegistrationData] = useState<RegistrationFormData>({
    name: '',
    email: '',
    institution: '',
    password: '',
    marketingConsent: false,
  });
  const [registerConfirmPassword, setRegisterConfirmPassword] = useState('');
  const [loginData, setLoginData] = useState<LoginFormData>({
    email: (initialLoginEmail ?? '').trim(),
    password: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [registrationErrors, setRegistrationErrors] = useState<
    Partial<Record<keyof RegistrationFormData | 'confirmPassword', string>>
  >({});
  const [loginErrors, setLoginErrors] = useState<Partial<Record<keyof LoginFormData, string>>>({});
  const [submissionError, setSubmissionError] = useState('');
  const [postRegisterSuccessMessage, setPostRegisterSuccessMessage] = useState(
    initialPostRegisterSuccessMessage?.trim() ?? '',
  );
  const [forgotPasswordCooldownSeconds, setForgotPasswordCooldownSeconds] = useState(0);
  const [forgotPasswordMessage, setForgotPasswordMessage] = useState('');
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [resendVerificationBusy, setResendVerificationBusy] = useState(false);
  useEffect(() => {
    if (forgotPasswordCooldownSeconds <= 0) return;
    const timer = window.setInterval(() => {
      setForgotPasswordCooldownSeconds((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [forgotPasswordCooldownSeconds]);

  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [showRegisterConfirmPassword, setShowRegisterConfirmPassword] = useState(false);

  if (!isOpen) return null;

  const validateRegistrationForm = (): boolean => {
    const newErrors: Partial<Record<keyof RegistrationFormData | 'confirmPassword', string>> = {};

    if (!registrationData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!registrationData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(registrationData.email)) {
      newErrors.email = 'Invalid email format';
    }

    const pwd = registrationData.password ?? '';
    if (!pwd) {
      newErrors.password = 'Password is required';
    } else if (pwd.length < MIN_PASSWORD_LENGTH) {
      newErrors.password = `Password must be at least ${MIN_PASSWORD_LENGTH} characters`;
    }

    if (pwd !== registerConfirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (requireRegistrationConsent && !registrationData.marketingConsent) {
      newErrors.marketingConsent = 'Please accept to create your account';
    }

    setRegistrationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateLoginForm = (): boolean => {
    const newErrors: Partial<Record<keyof LoginFormData, string>> = {};

    if (!loginData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!loginData.password) {
      newErrors.password = 'Password is required';
    }

    setLoginErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateLoginForm()) return;

    setIsSubmitting(true);
    setSubmissionError('');
    setPostRegisterSuccessMessage('');
    try {
      if (!onLoginSubmit) {
        throw new Error('Login is not configured for this app yet.');
      }

      await onLoginSubmit(loginData);
      setLoginErrors({});
      setLoginData({ email: '', password: '' });
    } catch (error) {
      console.error('Login error:', error);
      setSubmissionError(error instanceof Error ? error.message : 'Login failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!onForgotPasswordSubmit) return;
    if (forgotPasswordCooldownSeconds > 0) return;
    const email = loginData.email.trim();
    if (!email) {
      setLoginErrors((prev) => ({ ...prev, email: 'Enter your email to reset password' }));
      setForgotPasswordMessage('');
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setLoginErrors((prev) => ({ ...prev, email: 'Invalid email format' }));
      setForgotPasswordMessage('');
      return;
    }
    setIsSubmitting(true);
    setSubmissionError('');
    setForgotPasswordMessage('');
    try {
      await onForgotPasswordSubmit(email);
      setForgotPasswordMessage('If an account exists for this email, a reset link has been sent.');
      setForgotPasswordCooldownSeconds(30);
    } catch (error) {
      setSubmissionError(error instanceof Error ? error.message : 'Unable to send reset email');
    } finally {
      setIsSubmitting(false);
    }
  };

  const needsVerificationResend = submissionError
    .toLowerCase()
    .includes('please verify your email before signing in');

  const handleResendVerificationEmail = async () => {
    if (!onResendVerificationEmail || resendVerificationBusy) return;
    setResendVerificationBusy(true);
    try {
      await onResendVerificationEmail();
      setSubmissionError('Verification email sent. Check your inbox and spam folder.');
    } catch (error) {
      console.error('Resend verification error:', error);
      setSubmissionError(
        error instanceof Error ? error.message : 'Could not send verification email right now.'
      );
    } finally {
      setResendVerificationBusy(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateRegistrationForm()) return;

    setIsSubmitting(true);
    setSubmissionError('');
    const submitPayload: RegistrationFormData = {
      name: registrationData.name,
      email: registrationData.email,
      institution: registrationData.institution,
      password: registrationData.password,
      productKey: registrationData.productKey,
    };
    if (requireRegistrationConsent) {
      submitPayload.marketingConsent = registrationData.marketingConsent === true;
    }

    try {
      await onSubmit(submitPayload);
      const savedEmail = registrationData.email.trim();
      setRegistrationData({ name: '', email: '', institution: '', password: '', marketingConsent: false });
      setRegisterConfirmPassword('');
      setRegistrationErrors({});

      if (switchToLoginAfterSuccessfulRegistration) {
        setLoginData({ email: savedEmail, password: '' });
        setMode('login');
        setPostRegisterSuccessMessage('Account created. Sign in with your email and password.');
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error instanceof Error ? error.message : 'Account creation failed';
      const normalizedMessage = message.toLowerCase();
      const isEmailAlreadyExistsError =
        normalizedMessage.includes('already exists') ||
        normalizedMessage.includes('email-in-use') ||
        normalizedMessage.includes('email already in use');
      if (isEmailAlreadyExistsError) {
        const inlineMessage = 'An account with this email already exists. No new account was created.';
        setRegistrationErrors((prev) => ({
          ...prev,
          email: inlineMessage,
        }));
        setSubmissionError('');
      } else {
        setSubmissionError(message);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRegistrationInputChange = (field: keyof RegistrationFormData, value: string) => {
    setRegistrationData((prev) => ({ ...prev, [field]: value }));
    if (registrationErrors[field]) {
      setRegistrationErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (field === 'password') {
      if (registrationErrors.confirmPassword) {
        setRegistrationErrors((prev) => ({ ...prev, confirmPassword: undefined }));
      }
    }
    setSubmissionError('');
  };

  const handleMarketingConsentChange = (checked: boolean) => {
    setRegistrationData((prev) => ({ ...prev, marketingConsent: checked }));
    if (registrationErrors.marketingConsent) {
      setRegistrationErrors((prev) => ({ ...prev, marketingConsent: undefined }));
    }
    setSubmissionError('');
  };

  const handleConfirmPasswordChange = (value: string) => {
    setRegisterConfirmPassword(value);
    if (registrationErrors.confirmPassword) {
      setRegistrationErrors((prev) => ({ ...prev, confirmPassword: undefined }));
    }
    setSubmissionError('');
  };

  const handleLoginInputChange = (field: keyof LoginFormData, value: string) => {
    setLoginData((prev) => ({ ...prev, [field]: value }));
    if (loginErrors[field]) {
      setLoginErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    setSubmissionError('');
    // Do not clear postRegisterSuccessMessage here — browser autofill can fire change
    // events on mount and hide "check your email" guidance before the user sees it.
    setForgotPasswordMessage('');
  };

  const switchMode = (nextMode: DialogMode) => {
    setMode(nextMode);
    setSubmissionError('');
    setLoginErrors({});
    setRegistrationErrors({});
    setRegisterConfirmPassword('');
    setPostRegisterSuccessMessage('');
    setRegistrationData((prev) => ({ ...prev, marketingConsent: false }));
  };

  const goToRegisterTab = () => {
    if (onSwitchToRegister) {
      onSwitchToRegister();
    } else {
      switchMode('register');
    }
  };

  const goToLoginTab = () => {
    if (onSwitchToLogin) {
      onSwitchToLogin();
    } else {
      switchMode('login');
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const isAuthBranded = Boolean(registerBrandKicker);
  const showKickerLogo = isAuthBranded && Boolean(registerTitleLogoSrc);
  const headline = isAuthBranded ? title : mode === 'login' ? 'Log In' : title;
  const subtitleLine = isAuthBranded
    ? subtitle
    : mode === 'login'
      ? 'Sign in to continue using this product'
      : subtitle;

  return (
    <div className="registration-dialog-overlay" onClick={handleOverlayClick}>
      <div
        className={isAuthBranded ? 'registration-dialog registration-dialog--auth-branded' : 'registration-dialog'}
        role="dialog"
        aria-labelledby="dialog-title"
        aria-modal="true"
      >
        <div
          className={
            isAuthBranded ? 'registration-dialog-header registration-dialog-header--branded' : 'registration-dialog-header'
          }
        >
          {isAuthBranded &&
            (showKickerLogo && registerTitleLogoSrc ? (
              <div className="registration-dialog-kicker-row">
                <span className="registration-dialog-kicker-logo-wrap">
                  <img
                    src={registerTitleLogoSrc}
                    alt={registerTitleLogoAlt}
                    className="registration-dialog-kicker-logo"
                  />
                </span>
                <p className="registration-dialog-kicker registration-dialog-kicker--in-row">{registerBrandKicker}</p>
              </div>
            ) : (
              <p className="registration-dialog-kicker">{registerBrandKicker}</p>
            ))}
          <h2 id="dialog-title" className="registration-dialog-title">
            {headline}
          </h2>
          {Boolean(subtitleLine) && (
            <p className="registration-dialog-subtitle">{subtitleLine}</p>
          )}
        </div>

        {mode === 'login' ? (
          <form
            onSubmit={handleLogin}
            className={isAuthBranded ? 'registration-dialog-form registration-dialog-form--register' : 'registration-dialog-form'}
          >
            {postRegisterSuccessMessage && (
              <p className="registration-dialog-success" role="status" aria-live="polite">
                {postRegisterSuccessMessage}
              </p>
            )}
            <div className="registration-form-field">
              <label htmlFor="login-email">
                Email address <span className="required">*</span>
              </label>
              <input
                id="login-email"
                type="email"
                value={loginData.email}
                onChange={(e) => handleLoginInputChange('email', e.target.value)}
                className={loginErrors.email ? 'error' : ''}
                disabled={isSubmitting}
                required
                autoComplete="email"
              />
              {loginErrors.email && <span className="error-message">{loginErrors.email}</span>}
            </div>

            <div className="registration-form-field">
              <label htmlFor="login-password">
                Password <span className="required">*</span>
              </label>
              <div className="registration-password-row">
                <input
                  id="login-password"
                  type={showLoginPassword ? 'text' : 'password'}
                  value={loginData.password}
                  onChange={(e) => handleLoginInputChange('password', e.target.value)}
                  className={loginErrors.password ? 'error' : ''}
                  disabled={isSubmitting}
                  required
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="registration-password-toggle"
                  onClick={() => setShowLoginPassword((prev) => !prev)}
                  disabled={isSubmitting}
                  aria-label={showLoginPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showLoginPassword}
                  title={showLoginPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon crossed={showLoginPassword} />
                </button>
              </div>
              {loginErrors.password && <span className="error-message">{loginErrors.password}</span>}
            </div>
            {onForgotPasswordSubmit && (
              <div className="registration-login-forgot-wrap">
                <button
                  type="button"
                  className="registration-dialog-switch-button"
                  onClick={handleForgotPassword}
                  disabled={isSubmitting || forgotPasswordCooldownSeconds > 0}
                >
                  {forgotPasswordCooldownSeconds > 0
                    ? `Forgot password? (${forgotPasswordCooldownSeconds}s)`
                    : 'Forgot password?'}
                </button>
              </div>
            )}
            {!onForgotPasswordSubmit && onForgotPasswordClick && (
              <div className="registration-login-forgot-wrap">
                <button
                  type="button"
                  className="registration-dialog-switch-button"
                  onClick={onForgotPasswordClick}
                  disabled={isSubmitting}
                >
                  Forgot password?
                </button>
              </div>
            )}
            {forgotPasswordMessage && (
              <p className="registration-dialog-success" role="status" aria-live="polite">
                {forgotPasswordMessage}
              </p>
            )}

            {submissionError && (
              <p className="form-error-message">
                {submissionError}
                {needsVerificationResend && onResendVerificationEmail && (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="registration-inline-link-button"
                      onClick={() => void handleResendVerificationEmail()}
                      disabled={isSubmitting || resendVerificationBusy}
                    >
                      {resendVerificationBusy ? 'Sending...' : 'Resend email'}
                    </button>
                  </>
                )}
              </p>
            )}

            <div
              className={
                registerShowCancel
                  ? 'registration-dialog-actions'
                  : 'registration-dialog-actions registration-dialog-actions--register-only'
              }
            >
              {registerShowCancel && (
                <button
                  type="button"
                  onClick={onClose}
                  className="registration-button registration-button-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="registration-button registration-button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Logging in...' : 'Log In'}
              </button>
            </div>

            <p className="registration-dialog-switch">
              Don&apos;t have an account?{' '}
              <button
                type="button"
                className="registration-dialog-switch-button"
                onClick={goToRegisterTab}
                disabled={isSubmitting}
              >
                Create one
              </button>
            </p>
          </form>
        ) : (
          <form
            onSubmit={handleRegister}
            className={isAuthBranded ? 'registration-dialog-form registration-dialog-form--register' : 'registration-dialog-form'}
          >
            <div className="registration-form-field">
              <label htmlFor="name">
                Full name <span className="required">*</span>
              </label>
              <input
                id="name"
                type="text"
                value={registrationData.name}
                onChange={(e) => handleRegistrationInputChange('name', e.target.value)}
                className={registrationErrors.name ? 'error' : ''}
                disabled={isSubmitting}
                required
                autoComplete="name"
              />
              {registrationErrors.name && <span className="error-message">{registrationErrors.name}</span>}
            </div>

            <div className="registration-form-field">
              <label htmlFor="email">
                Email address <span className="required">*</span>
              </label>
              <input
                id="email"
                type="email"
                value={registrationData.email}
                onChange={(e) => handleRegistrationInputChange('email', e.target.value)}
                className={registrationErrors.email ? 'error' : ''}
                disabled={isSubmitting}
                required
                autoComplete="email"
              />
              {registrationErrors.email && <span className="error-message">{registrationErrors.email}</span>}
            </div>

            <div className="registration-form-field">
              <label htmlFor="institution">Institution</label>
              <input
                id="institution"
                type="text"
                value={registrationData.institution}
                onChange={(e) => handleRegistrationInputChange('institution', e.target.value)}
                disabled={isSubmitting}
                autoComplete="organization"
              />
            </div>

            <div className="registration-form-field">
              <label htmlFor="register-password">
                Password <span className="required">*</span>
              </label>
              <div className="registration-password-row">
                <input
                  id="register-password"
                  type={showRegisterPassword ? 'text' : 'password'}
                  value={registrationData.password ?? ''}
                  onChange={(e) => handleRegistrationInputChange('password', e.target.value)}
                  className={registrationErrors.password ? 'error' : ''}
                  disabled={isSubmitting}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="registration-password-toggle"
                  onClick={() => setShowRegisterPassword((prev) => !prev)}
                  disabled={isSubmitting}
                  aria-label={showRegisterPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showRegisterPassword}
                  title={showRegisterPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon crossed={showRegisterPassword} />
                </button>
              </div>
              {registrationErrors.password && <span className="error-message">{registrationErrors.password}</span>}
            </div>

            <div className="registration-form-field">
              <label htmlFor="register-confirm-password">
                Confirm password <span className="required">*</span>
              </label>
              <div className="registration-password-row">
                <input
                  id="register-confirm-password"
                  type={showRegisterConfirmPassword ? 'text' : 'password'}
                  value={registerConfirmPassword}
                  onChange={(e) => handleConfirmPasswordChange(e.target.value)}
                  className={registrationErrors.confirmPassword ? 'error' : ''}
                  disabled={isSubmitting}
                  required
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="registration-password-toggle"
                  onClick={() => setShowRegisterConfirmPassword((prev) => !prev)}
                  disabled={isSubmitting}
                  aria-label={showRegisterConfirmPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showRegisterConfirmPassword}
                  title={showRegisterConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  <EyeIcon crossed={showRegisterConfirmPassword} />
                </button>
              </div>
              {registrationErrors.confirmPassword && (
                <span className="error-message">{registrationErrors.confirmPassword}</span>
              )}
            </div>

            {requireRegistrationConsent && (
              <div
                className={`registration-form-field registration-form-consent${registrationErrors.marketingConsent ? ' registration-form-consent--error' : ''}`}
              >
                <div className="registration-form-consent-row">
                  <input
                    id="registration-privacy-consent"
                    type="checkbox"
                    checked={Boolean(registrationData.marketingConsent)}
                    onChange={(e) => handleMarketingConsentChange(e.target.checked)}
                    disabled={isSubmitting}
                    aria-invalid={Boolean(registrationErrors.marketingConsent)}
                    aria-describedby={registrationErrors.marketingConsent ? 'registration-privacy-consent-error' : undefined}
                  />
                  <label htmlFor="registration-privacy-consent" className="registration-form-consent-copy">
                    {resolvedConsentText}
                  </label>
                </div>
                {registrationErrors.marketingConsent && (
                  <span className="error-message" id="registration-privacy-consent-error" role="alert">
                    {registrationErrors.marketingConsent}
                  </span>
                )}
              </div>
            )}

            {submissionError && <p className="form-error-message">{submissionError}</p>}

            <div
              className={
                registerShowCancel
                  ? 'registration-dialog-actions'
                  : 'registration-dialog-actions registration-dialog-actions--register-only'
              }
            >
              {registerShowCancel && (
                <button
                  type="button"
                  onClick={onClose}
                  className="registration-button registration-button-secondary"
                  disabled={isSubmitting}
                >
                  Cancel
                </button>
              )}
              <button
                type="submit"
                className="registration-button registration-button-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Creating account...' : 'Create Account'}
              </button>
            </div>

            <p className="registration-dialog-switch">
              Already have an account?{' '}
              <button
                type="button"
                className="registration-dialog-switch-button"
                onClick={goToLoginTab}
                disabled={isSubmitting}
              >
                Log in
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
